import { lazy, type ComponentType } from "react";

const RELOAD_FLAG = "lovable:chunk-reload";
/** Minimum gap between automatic reloads, so recovery can never loop. */
export const AUTO_RELOAD_WINDOW_MS = 60_000;

export const isModuleLoadError = (error?: unknown): boolean => {
  const message =
    (error as { message?: string } | undefined)?.message ??
    (typeof error === "string" ? error : "");
  if (!message) return false;
  return (
    /Importing a module script failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Unable to preload CSS/i.test(message) ||
    /Loading chunk .* failed/i.test(message) ||
    /Loading CSS chunk/i.test(message)
  );
};

const lastReloadAt = (): number => {
  try {
    return Number(sessionStorage.getItem(RELOAD_FLAG)) || 0;
  } catch {
    return 0;
  }
};

/** True when an automatic reload already happened within the last minute. */
export const recentlyAutoReloaded = (now = Date.now()): boolean => {
  const at = lastReloadAt();
  return at > 0 && now - at < AUTO_RELOAD_WINDOW_MS;
};

/** Back-compat alias: "we just tried a fresh reload and it still failed". */
export const hasAlreadyReloadedForChunkError = (): boolean => recentlyAutoReloaded();

/** Clear the guard once a page file has loaded fine, so the next publish can self-heal. */
export const markChunkLoadHealthy = () => {
  try {
    if (lastReloadAt() && !recentlyAutoReloaded()) sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // ignore
  }
};

/** Reload with a cache-busting marker; at most once per minute unless forced. */
export const cacheBustingReload = (force = false) => {
  if (!force && recentlyAutoReloaded()) return false;
  try {
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable — still attempt a single reload
  }
  const url = new URL(window.location.href);
  url.searchParams.set("_r", String(Date.now()));
  window.location.replace(url.toString());
  return true;
};

/**
 * React.lazy that self-heals stale page files (after a new publish) with a
 * guarded cache-busting reload. Retrying the same URL is pointless: browsers
 * cache failed module fetches, so we go straight to a fresh page load.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      markChunkLoadHealthy();
      return mod;
    } catch (error) {
      if (isModuleLoadError(error)) cacheBustingReload();
      throw error;
    }
  });
}
